import { useFormContext } from 'react-hook-form';
import { TransactionFormData } from '../../../shared/types';
import useFetchDdsData, { DdsField } from './useFetchDdsData';
import * as yup from 'yup';
import { ALPHANUMERIC_REGEX } from './TransactionForm';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../hooks';

export const useModalDdsSchema = () => {
  const { t } = useTranslation();

  return yup.array().of(
    yup.object().shape({
      reference: yup
        .string()
        .required(
          t('validations.required', {
            field: t('transactions.form.referenceNumber'),
          })
        )
        .matches(ALPHANUMERIC_REGEX, t('validations.alphanumeric'))
        .max(
          14,
          t('validations.maxLength', {
            field: t('transactions.form.referenceNumber'),
            length: 14,
          })
        ),
      verification: yup
        .string()
        .required(
          t('validations.required', {
            field: t('transactions.form.verificationNumber'),
          })
        )
        .matches(ALPHANUMERIC_REGEX, t('validations.alphanumeric'))
        .min(
          5,
          t('validations.minLength', {
            field: t('transactions.form.verificationNumber'),
            length: 5,
          })
        )
        .max(
          35,
          t('validations.maxLength', {
            field: t('transactions.form.verificationNumber'),
            length: 35,
          })
        ),
    })
  );
};

const DDS_NOT_FOUND_ERROR_MESSAGE =
  'Could not find DDS with the provided reference and verification numbers';

type UseValidateDdsProps = {
  onSuccessAll: () => void;
  appendDds: (dds: DdsField) => void;
};

type UseValidateDdsResult = {
  validateDds: () => Promise<void>;
  isValidating: boolean;
};

const useValidateDds = ({
  onSuccessAll,
  appendDds,
}: UseValidateDdsProps): UseValidateDdsResult => {
  const {
    watch,
    setError,
    trigger,
    formState: { errors },
    clearErrors,
  } = useFormContext<TransactionFormData>();
  const { showError } = useNotification();
  const { t } = useTranslation();

  const isDuplicate = (index: number) => {
    const existingDds = watch('importedDds');
    const modalDds = watch('modalDds');

    // Check for duplicates in importedDds
    const isDuplicateInImported = existingDds?.some(
      (dds) =>
        dds.reference === watch(`modalDds.${index}.reference`) &&
        dds.verification === watch(`modalDds.${index}.verification`)
    );

    // Check for duplicates within modalDds
    const isDuplicateInModal = modalDds?.some(
      (dds, i) =>
        i !== index && // Don't compare with self
        dds.reference === watch(`modalDds.${index}.reference`) &&
        dds.verification === watch(`modalDds.${index}.verification`)
    );

    return isDuplicateInImported || isDuplicateInModal;
  };

  const setErrorForReferenceStatement = (index: number, message: string) => {
    setError(`modalDds.${index}.reference`, {
      message: ' ',
    });
    setError(`modalDds.${index}.verification`, {
      message: ' ',
    });
    setError(`root.modalDds.${index}`, {
      message,
    });
  };

  const ddsList = useFetchDdsData(watch('modalDds'), {
    enabled: false,
    onSuccessCb: (_, index) => {
      const isDuplicateEntry = isDuplicate(index);

      if (isDuplicateEntry) {
        setErrorForReferenceStatement(
          index,
          t('transactions.form.referenceAlreadyAdded')
        );
        return;
      }

      if (!errors?.root?.modalDds) {
        appendDds({
          reference: watch(`modalDds.${index}.reference`),
          verification: watch(`modalDds.${index}.verification`),
        });
        onSuccessAll();
      }
    },
    onErrorCb: (error, index) => {
      if (error.statusCode === 429) {
        showError(t('error.tooManyRequests'));
      }

      if (error.message === DDS_NOT_FOUND_ERROR_MESSAGE) {
        setErrorForReferenceStatement(index, DDS_NOT_FOUND_ERROR_MESSAGE);
      }
    },
  });

  const validateDds = async () => {
    clearErrors();
    const isValid = await trigger('modalDds');

    if (!isValid || errors?.root?.modalDds) {
      return;
    }

    ddsList.forEach((query) => {
      query.refetch();
    });
  };

  return { validateDds, isValidating: ddsList.some((d) => d.isFetching) };
};

export default useValidateDds;
